import { Box, Button, Typography } from '@mui/material';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import { observer } from 'mobx-react';
import React from 'react';
import { BusinessAction } from 'services/businessBilling/contract';
import { useStores } from 'stores/storeInitializer';
import { useSecondsLeft } from './useSecondsLeft';

interface ErrorActionsProps {
    actions: BusinessAction[]; // Only these are rendered, in the order of the ticket.
    retryAvailableAt: number | null; // When Try again becomes available; null: now.
    retriesLeft: number; // Shown next to Try again; at 0 Try again is not offered at all.
    disabled?: boolean; // A lookup is running.
    onEditCui: () => void;
    onRetry: () => void;
    onSwitchToIndividual: () => void;
    onContinueWithout: () => void;
}

const visuallyHidden = {
    position: 'absolute',
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
} as const;

/**
 * The recovery actions the server permits under an error. It only renders the list it is given: it never decides
 * what is permitted. Nothing here retries by itself; Try again waits for its countdown and then for a click.
 */
export const ErrorActions: React.FC<ErrorActionsProps> = observer(
    ({ actions, retryAvailableAt, retriesLeft, disabled = false, onEditCui, onRetry, onSwitchToIndividual, onContinueWithout }) => {
        const { store } = useStores();
        const secondsLeft = useSecondsLeft(retryAvailableAt);
        const has = (action: BusinessAction) => actions.includes(action);
        const showRetry = has('RETRY_LOOKUP') && retriesLeft > 0;
        const waiting = secondsLeft > 0;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {has('RESTART_FROM_TERMINAL') ? (
                    <Box
                        component="section"
                        aria-label={store.L(LocalizationKeys.BusinessRestartTitle)}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'background.paper' }}
                    >
                        <Typography variant="subtitle1" component="h2" fontWeight={700} gutterBottom>
                            {store.L(LocalizationKeys.BusinessRestartTitle)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {store.L(LocalizationKeys.BusinessRestartBody)}
                        </Typography>
                    </Box>
                ) : null}
                {has('EDIT_CUI') ? (
                    <Button type="button" variant="outlined" onClick={onEditCui}>
                        {store.L(LocalizationKeys.BusinessActionEditCui)}
                    </Button>
                ) : null}
                {showRetry ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Button type="button" variant="outlined" disabled={disabled || waiting} onClick={onRetry}>
                            {waiting
                                ? store.L(LocalizationKeys.BusinessActionRetryIn, secondsLeft)
                                : store.L(LocalizationKeys.BusinessActionRetry)}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                            {store.L(LocalizationKeys.BusinessActionAttemptsLeft, retriesLeft)}
                        </Typography>
                        {/* Told once, when the wait is over; the ticking itself is not announced. */}
                        <Box role="status" aria-live="polite" sx={visuallyHidden}>
                            {waiting ? '' : store.L(LocalizationKeys.BusinessActionRetry)}
                        </Box>
                    </Box>
                ) : null}
                {has('SWITCH_TO_INDIVIDUAL') ? (
                    <Button type="button" variant="outlined" onClick={onSwitchToIndividual}>
                        {store.L(LocalizationKeys.BusinessActionSwitchToIndividual)}
                    </Button>
                ) : null}
                {has('CONTINUE_WITHOUT_BILLING') ? (
                    <Button type="button" variant="outlined" onClick={onContinueWithout}>
                        {store.L(LocalizationKeys.BillingDetailsContinueWithout)}
                    </Button>
                ) : null}
            </Box>
        );
    },
);

export default ErrorActions;
